/* * Version: 1.0.0
 * Letzte Änderung: 18.09.2026, 15:09
 * Änderungshistorie: Initiale Erstellung der Benutzeroberfläche zur Verwaltung der Administratoren-Konten.
 * Copyright: BAIT-Tech
 */
import React, { useState, useEffect, useContext } from 'react';
import apiClient from '../api';
import { AuthContext } from '../context/AuthContext';
import { toast } from 'react-toastify';

const UserManagementScreen = () => {
  const { userInfo } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // States für das Formular zum Anlegen eines neuen Benutzers
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  
  // States für das Passwort-Reset-Formular
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');

  // Lädt die Liste aller Benutzer vom Server
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get('/api/users');
      setUsers(data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Fehler beim Laden der Benutzer.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Erstellt einen neuen Administrator
  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newEmail || !newPassword) {
      return toast.warn('Bitte E-Mail und Passwort eingeben.');
    }
    if (newPassword.length < 6) {
      return toast.warn('Das Passwort muss mindestens 6 Zeichen lang sein.');
    }

    try {
      await apiClient.post('/api/users', { email: newEmail, password: newPassword });
      toast.success('Benutzer erfolgreich angelegt.');
      setNewEmail('');
      setNewPassword('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Fehler beim Anlegen des Benutzers.');
    }
  };

  // Löscht einen Administrator (Schutz vor Selbstlöschung wird auch im Backend geprüft)
  const handleDeleteUser = async (id) => {
    if (window.confirm('Möchtest du diesen Administrator wirklich entfernen?')) {
      try {
        await apiClient.delete(`/api/users/${id}`);
        toast.success('Benutzer gelöscht.');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Fehler beim Löschen.');
      }
    }
  };

  // Überschreibt das Passwort eines bestehenden Benutzers
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (resetPassword.length < 6) {
      return toast.warn('Das neue Passwort muss mindestens 6 Zeichen lang sein.');
    }
    
    try {
      await apiClient.put(`/api/users/${resetUserId}/password`, { newPassword: resetPassword });
      toast.success('Passwort erfolgreich überschrieben.');
      setResetUserId(null);
      setResetPassword('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Fehler beim Ändern des Passworts.');
    }
  };

  if (loading) return <div className="screen-container">Lade Benutzerdaten...</div>;

  return (
    <div className="screen-container">
      <h2>Benutzerverwaltung</h2>
      
      <div className="layout-grid">
        <div className="grid-column">
          <h3>Aktuelle Administratoren</h3>
          <table>
            <thead>
              <tr>
                <th>E-Mail</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td>{user.email} {user._id === userInfo?._id && <strong>(Du)</strong>}</td>
                  <td>
                    <button 
                      className="btn-secondary-action" 
                      style={{ marginRight: '10px' }}
                      onClick={() => setResetUserId(user._id)}
                    >
                      Passwort ändern
                    </button>
                    <button 
                      className="btn-delete-small"
                      onClick={() => handleDeleteUser(user._id)}
                      disabled={user._id === userInfo?._id}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid-column">
          {resetUserId ? (
            <div className="form-container-box" style={{ border: '2px solid #007bff' }}>
              <h4>Neues Passwort vergeben</h4>
              <p>Für: {users.find(u => u._id === resetUserId)?.email}</p>
              <form onSubmit={handlePasswordReset}>
                <div className="form-group">
                  <label>Neues Passwort</label>
                  <input 
                    type="password" 
                    value={resetPassword} 
                    onChange={(e) => setResetPassword(e.target.value)} 
                    required 
                  />
                </div>
                <div className="form-buttons">
                  <button type="submit">Passwort speichern</button>
                  <button type="button" className="btn-cancel" onClick={() => { setResetUserId(null); setResetPassword(''); }}>Abbrechen</button>
                </div>
              </form>
            </div>
          ) : (
            <div className="form-container-box">
              <h4>Neuen Administrator anlegen</h4>
              <form onSubmit={handleCreateUser}>
                <div className="form-group">
                  <label>E-Mail Adresse</label>
                  <input 
                    type="email" 
                    value={newEmail} 
                    onChange={(e) => setNewEmail(e.target.value)} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Initiales Passwort</label>
                  <input 
                    type="password" 
                    value={newPassword} 
                    onChange={(e) => setNewPassword(e.target.value)} 
                    required 
                  />
                </div>
                <button type="submit">Benutzer anlegen</button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagementScreen;